/**
 * Daring Futures - Algorithm v7.0
 * Evidence-Based Transformation Value Recovery Calculations
 *
 * Research Foundation:
 * - McKinsey & Company (2021): "The state of organizations 2021"
 * - Skews, S. J. (2018): "Acceptance and commitment coaching: A meta-analysis"
 *
 * Specification: docs/algorithm/ALGORITHM_IMPLEMENTATION_SPECIFICATION_v7.0.0.md
 *
 * Copyright © 2026 Daring Futures. All rights reserved.
 * Proprietary and confidential. Unauthorized use is prohibited.
 *
 * History:
 * - v2.0 (v4.0.0): Initial evidence-based algorithm implementation
 * - v5.0.0: Database-driven configuration with fallback constants
 * - v7.0.0: Consolidated algorithm file, standardised versioning
 */

// ============================================================================
// DATABASE-DRIVEN CONFIGURATION (v5.0.0+)
// ============================================================================

// Cache for diagnostic settings loaded from database
let diagnosticSettingsCache = null;

/**
 * Load diagnostic settings from database API
 * Called on app initialization
 * @returns {Promise<Object>} Diagnostic settings object
 */
export async function loadDiagnosticSettings() {
  try {
    const response = await fetch('/api/diagnostic-settings');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();

    if (data.success && data.settings) {
      diagnosticSettingsCache = data.settings;
      console.log('✅ Diagnostic settings loaded from database', {
        count: data.count,
        version: '7.0.0'
      });
      return diagnosticSettingsCache;
    } else {
      throw new Error('Invalid response format from diagnostic-settings API');
    }
  } catch (error) {
    console.warn('⚠️ Failed to load diagnostic settings from database, using fallbacks', error.message);
    diagnosticSettingsCache = null;
    return FALLBACK_CONSTANTS;
  }
}

/**
 * Get current settings (from cache or fallback)
 * @returns {Object} Current diagnostic settings
 */
function getSettings() {
  return diagnosticSettingsCache || FALLBACK_CONSTANTS;
}

// ============================================================================
// CORE CONSTANTS - McKinsey & Skews Research
// ============================================================================

// McKinsey & Company (2021) - Transformation Value Loss
export const VALUE_LOSS_PERCENTAGE = 0.43;
export const AVERAGE_LOSS_RATE = 0.43;           // Average transformations lose 43% of value
export const SUCCESSFUL_LOSS_RATE = 0.33;        // Successful transformations lose 33% of value
export const AVERAGE_CAPTURE_RATE = 0.57;        // 1 - 0.43 = 57% captured on average
export const SUCCESSFUL_CEILING = 0.67;          // 1 - 0.33 = 67% maximum empirical capture rate
export const MAX_RECOVERY_RATE = 0.233;          // (0.67 - 0.57) / 0.43 = 23.3% max recovery
export const REALIZATION_WINDOW_MONTHS = 18;

// Value Loss Attribution (derived from organizational research)
export const SYSTEM_LOSS_WEIGHT = 0.685;         // 68.5% of loss is system-addressable
export const LEADERSHIP_LOSS_WEIGHT = 0.315;     // 31.5% of loss is leadership-addressable

// Skews (2018) - ACT Coaching Effect Size
export const ACT_COACHING_MULTIPLIER = 1.08;     // η² = 0.08 → 8% effect size on mental health

// Recoverability factors by role (legacy)
export const RECOVERABILITY_FACTORS = {
  'Transformation Leader': 0.55,
  'Senior Leader': 0.62
};

// ============================================================================
// PERSONA BASELINES
// ============================================================================

export const PERSONA_BASELINES = {
  mark: {
    name: 'Mark Ellison',
    dcf: 0.70,                                   // 98/140 - Slightly above average org capability
    lpff: 0.55,                                  // 55/100 - Moderate psychological inflexibility
    role: 'Transformation Director',
    strain: 'System Overload',
    description: 'Organizational dependencies are overwhelming. The system is the primary constraint.'
  },
  sarah: {
    name: 'Sarah Lawson',
    dcf: 0.65,                                   // 91/140 - Average organizational capability
    lpff: 0.65,                                  // 65/100 - Higher psychological inflexibility
    role: 'Chief Operating Officer',
    strain: 'Self Overload',
    description: 'Personal psychological burden is high. Leadership anxiety is the primary constraint.'
  }
};

// Fallback constants object (used if database fetch fails)
const FALLBACK_CONSTANTS = {
  average_loss_rate: AVERAGE_LOSS_RATE,
  successful_loss_rate: SUCCESSFUL_LOSS_RATE,
  average_capture_rate: AVERAGE_CAPTURE_RATE,
  successful_ceiling: SUCCESSFUL_CEILING,
  max_recovery_rate: MAX_RECOVERY_RATE,
  system_loss_weight: SYSTEM_LOSS_WEIGHT,
  leadership_loss_weight: LEADERSHIP_LOSS_WEIGHT,
  act_coaching_multiplier: ACT_COACHING_MULTIPLIER,
  mark_dcf: PERSONA_BASELINES.mark.dcf,
  mark_lpff: PERSONA_BASELINES.mark.lpff,
  mark_role: PERSONA_BASELINES.mark.role,
  mark_strain: PERSONA_BASELINES.mark.strain,
  mark_description: PERSONA_BASELINES.mark.description,
  sarah_dcf: PERSONA_BASELINES.sarah.dcf,
  sarah_lpff: PERSONA_BASELINES.sarah.lpff,
  sarah_role: PERSONA_BASELINES.sarah.role,
  sarah_strain: PERSONA_BASELINES.sarah.strain,
  sarah_description: PERSONA_BASELINES.sarah.description
};

// ============================================================================
// DEPENDENCY WEIGHTS (for radar chart and insights)
// ============================================================================

export const DEPENDENCY_WEIGHTS = [
  { id: 1, name: 'Strategic clarity and value case', weight: 9 },
  { id: 2, name: 'Portfolio governance and decision rights', weight: 8 },
  { id: 3, name: 'Risk and adaptive planning maturity', weight: 8 },
  { id: 4, name: 'Delivery flow and interdependency management', weight: 9 },
  { id: 5, name: 'Operational performance management and data quality', weight: 7 },
  { id: 6, name: 'Change implementation and BA/DM capability', weight: 8 },
  { id: 7, name: 'Stakeholder network and sponsorship strength', weight: 8 },
  { id: 8, name: 'Leadership and team dynamics', weight: 9 },
  { id: 9, name: 'Culture, communication and narrative truth', weight: 8 },
  { id: 10, name: 'Hidden work and dependence load', weight: 10 },
  { id: 11, name: 'Organisational complexity and dependency density', weight: 7 },
  { id: 12, name: 'Transformation fatigue and history', weight: 7 },
  { id: 13, name: 'Meeting and cadence architecture', weight: 7 },
  { id: 14, name: 'Talent availability and critical role stability', weight: 8 }
];

// ============================================================================
// ZONE UTILITIES
// ============================================================================

export function getZoneForScore(score) {
  if (score >= 0 && score <= 4) return 'compounded';
  if (score >= 5 && score <= 7) return 'clouded';
  return 'clear';
}

export function getZoneInterpretation(zone) {
  const interpretations = {
    'compounded': {
      title: 'Structural Constraint',
      description: 'Value leakage likely. Recoverability suppressed. This area needs focused intervention early.',
      color: '#ef4444'
    },
    'clouded': {
      title: 'Capable but Inconsistent',
      description: 'Can support recoverability, but becomes fragile under pressure. Needs reinforcement.',
      color: '#f59e0b'
    },
    'clear': {
      title: 'Enabler',
      description: 'Increases recoverability. Prioritise maintaining this as an organisational strength.',
      color: '#10b981'
    }
  };

  return interpretations[zone];
}

// ============================================================================
// SYSTEM BURDEN DIAGNOSTIC (Algorithm v7.0)
// ============================================================================

/**
 * Calculate Dependency Impact Factor (legacy method)
 * @param {Object} scores - Object with score1-score14 values
 * @returns {number} Dependency impact factor (0-1)
 */
export function calculateDependencyImpactFactor(scores) {
  let totalWeightedContribution = 0;
  let totalWeight = 0;

  DEPENDENCY_WEIGHTS.forEach(dep => {
    const score = scores[`score${dep.id}`] || 0;
    totalWeightedContribution += dep.weight * score;
    totalWeight += dep.weight;
  });

  const dependencyImpactFactor = totalWeightedContribution / (totalWeight * 10);
  return dependencyImpactFactor;
}

/**
 * Calculate transformation value (legacy method)
 * @param {number} valueAtStake - Value at stake
 * @param {string} role - Role type
 * @param {Object} dependencyScores - Dependency scores
 * @returns {Object} Calculation results
 */
export function calculateTransformationValue(valueAtStake, role, dependencyScores) {
  const valueLost = valueAtStake * VALUE_LOSS_PERCENTAGE;
  const recoverabilityFactorPercentage = RECOVERABILITY_FACTORS[role] || 0.55;
  const valueTheoreticallyRecoverable = valueLost * recoverabilityFactorPercentage;
  const dependencyImpactFactor = calculateDependencyImpactFactor(dependencyScores);
  const modifiedRecoverabilityPercentage = recoverabilityFactorPercentage * dependencyImpactFactor;
  const valueCurrentCapabilityRecoverable = valueLost * modifiedRecoverabilityPercentage;
  const valueDeficit = valueTheoreticallyRecoverable - valueCurrentCapabilityRecoverable;

  return {
    valueAtStake,
    valueLost,
    recoverabilityFactorPercentage,
    valueTheoreticallyRecoverable,
    dependencyImpactFactor,
    modifiedRecoverabilityPercentage,
    valueCurrentCapabilityRecoverable,
    valueDeficit,
    role
  };
}

/**
 * Calculate System Burden results using v7.0 algorithm
 *
 * Focus: Organizational capability (DCF varies, LPFF held constant at persona baseline)
 *
 * @param {number} transformationValue - Value at stake (£M)
 * @param {Object} dependencyScores - 14 dependency scores { dep1: 0-10, ..., dep14: 0-10 }
 * @param {string} personaId - 'mark' or 'sarah'
 * @returns {Object} Calculation results with all v7.0 metrics
 */
export function calculateSystemBurdenV2(transformationValue, dependencyScores, personaId = 'mark') {
  // Get settings (from database cache or fallback)
  const settings = getSettings();

  // Get persona baseline LPFF (held constant for system burden)
  const lpff = parseFloat(settings[`${personaId}_lpff`]);
  const personaRole = settings[`${personaId}_role`];
  const personaStrain = settings[`${personaId}_strain`];

  if (!lpff || !personaRole) {
    throw new Error(`Invalid persona ID: ${personaId}. Must be 'mark' or 'sarah'.`);
  }

  // Step 1: Calculate total value loss (McKinsey 2021)
  const totalLoss = transformationValue * parseFloat(settings.average_loss_rate);

  // Step 2: Attribute value loss to system vs leadership
  const systemLoss = totalLoss * parseFloat(settings.system_loss_weight);
  const leadershipLoss = totalLoss * parseFloat(settings.leadership_loss_weight);

  // Step 3: Calculate Dependency Competence Factor (DCF) - WEIGHTED FORMULA
  // DCF = Σ(weight[i] × score[i]) / (totalWeight × 10)
  // Higher scores = better capability = higher DCF
  let totalWeightedContribution = 0;
  let totalWeight = 0;
  DEPENDENCY_WEIGHTS.forEach(dep => {
    const score = dependencyScores[`dep${dep.id}`] || dependencyScores[`score${dep.id}`] || 0;
    totalWeightedContribution += dep.weight * score;
    totalWeight += dep.weight;
  });
  const dcf = totalWeightedContribution / (totalWeight * 10);  // totalWeight = 113, divisor = 1130

  // Step 4: Calculate Combined Recovery Factor (CRF)
  const systemComponent = dcf * parseFloat(settings.system_loss_weight);
  const leadershipComponent = lpff * parseFloat(settings.leadership_loss_weight) * parseFloat(settings.act_coaching_multiplier);
  const crf = systemComponent + leadershipComponent;

  // Step 5: Apply maximum recovery ceiling (23.3%)
  const recoverableValue = totalLoss * crf * parseFloat(settings.max_recovery_rate);

  // Step 6: Calculate recovery points added
  const recoveryPoints = recoverableValue / transformationValue;

  // Step 7: Calculate final capture rate
  const finalCaptureRate = parseFloat(settings.average_capture_rate) + recoveryPoints;

  // Step 8: Validate against McKinsey ceiling (67%)
  const successfulCeiling = parseFloat(settings.successful_ceiling);
  const isValid = finalCaptureRate <= successfulCeiling;
  const exceedsBy = isValid ? 0 : finalCaptureRate - successfulCeiling;

  return {
    // Value metrics
    transformationValue,
    totalLoss,
    systemLoss,
    leadershipLoss,

    // Factor scores
    dcf,
    lpff,
    lpffBaseline: lpff,  // For database storage
    crf,

    // Recovery calculation
    recoverableValue,
    recoveryPoints,
    finalCaptureRate,

    // Validation
    isValid,
    exceedsBy,
    mckinseySuccessfulCeiling: successfulCeiling,

    // Persona info
    personaId,
    personaRole,
    personaStrain,

    // Algorithm metadata
    algorithmVersion: '7.0'
  };
}

/**
 * Get top 3 weakest dependencies for intervention priorities
 *
 * @param {Object} dependencyScores - 14 dependency scores
 * @returns {Array} Top 3 dependencies with highest scores (most frequent problems)
 */
export function getTopWeakestDependencies(dependencyScores) {
  const dependencyNames = [
    'Strategic clarity and value case',
    'Portfolio governance and decision rights',
    'Risk and adaptive planning maturity',
    'Delivery flow and interdependency management',
    'Operational performance management and data quality',
    'Change implementation and BA/DM capability',
    'Stakeholder network and sponsorship strength',
    'Leadership and team dynamics',
    'Culture, communication and narrative truth',
    'Hidden work and dependence load',
    'Organisational complexity and dependency density',
    'Transformation fatigue and history',
    'Meeting and cadence architecture',
    'Talent availability and critical role stability'
  ];

  const scoredDependencies = dependencyNames.map((name, index) => ({
    id: index + 1,
    name,
    score: dependencyScores[`dep${index + 1}`] || 0
  }));

  // Sort by score DESCENDING (highest score = most frequent problem = weakest dependency)
  scoredDependencies.sort((a, b) => b.score - a.score);

  return scoredDependencies.slice(0, 3);
}

/**
 * Get top insights (legacy method for DiagnosticContainer)
 * @param {Object} scores - Scores object with score1-score14
 * @returns {Array} Top 3 insights
 */
export function getTopInsights(scores) {
  const scoredDependencies = DEPENDENCY_WEIGHTS.map(dep => ({
    ...dep,
    score: scores[`score${dep.id}`] || 0,
    weightedImpact: dep.weight * (10 - (scores[`score${dep.id}`] || 0))
  }));

  scoredDependencies.sort((a, b) => b.weightedImpact - a.weightedImpact);
  return scoredDependencies.slice(0, 3);
}

// ============================================================================
// LEADERSHIP BURDEN DIAGNOSTIC (Algorithm v7.0)
// ============================================================================

/**
 * Calculate Leadership Burden results using v7.0 algorithm
 *
 * Focus: Leadership psychological flexibility (LPFF varies, DCF held constant at persona baseline)
 *
 * @param {number} transformationValue - Value at stake (£M)
 * @param {Object} anxietyScores - 10 anxiety scores { anxiety1: 0-10, ..., anxiety10: 0-10 }
 * @param {string} personaId - 'mark' or 'sarah'
 * @returns {Object} Calculation results with all v7.0 metrics
 */
export function calculateLeadershipBurdenV2(transformationValue, anxietyScores, personaId = 'mark') {
  // Get settings (from database cache or fallback)
  const settings = getSettings();

  // Get persona baseline DCF (held constant for leadership burden)
  const dcf = parseFloat(settings[`${personaId}_dcf`]);
  const personaRole = settings[`${personaId}_role`];
  const personaStrain = settings[`${personaId}_strain`];

  if (!dcf || !personaRole) {
    throw new Error(`Invalid persona ID: ${personaId}. Must be 'mark' or 'sarah'.`);
  }

  // Step 1: Calculate total value loss (McKinsey 2021)
  const totalLoss = transformationValue * parseFloat(settings.average_loss_rate);

  // Step 2: Attribute value loss to system vs leadership
  const systemLoss = totalLoss * parseFloat(settings.system_loss_weight);
  const leadershipLoss = totalLoss * parseFloat(settings.leadership_loss_weight);

  // Step 3: Calculate Leadership Psychological Flexibility Factor (LPFF) from anxiety scores
  // IMPORTANT: Scores are inverted because questions ask "How often does problem occur?"
  // where 10 = "Constantly" (worst) and 0 = "Never" (best)
  // We invert so high problem frequency → low LPFF → less recoverable value
  const scoreValues = Object.values(anxietyScores);
  const invertedScore = scoreValues.reduce((sum, score) => sum + (10 - score), 0);
  const lpff = invertedScore / 100;  // 10 scores × 10 max = 100

  // Step 4: Calculate Combined Recovery Factor (CRF) with ACT multiplier
  const systemComponent = dcf * parseFloat(settings.system_loss_weight);
  const leadershipComponent = lpff * parseFloat(settings.leadership_loss_weight) * parseFloat(settings.act_coaching_multiplier);
  const crf = systemComponent + leadershipComponent;

  // Step 5: Apply maximum recovery ceiling (23.3%)
  const recoverableValue = totalLoss * crf * parseFloat(settings.max_recovery_rate);

  // Step 6: Calculate recovery points added
  const recoveryPoints = recoverableValue / transformationValue;

  // Step 7: Calculate final capture rate
  const finalCaptureRate = parseFloat(settings.average_capture_rate) + recoveryPoints;

  // Step 8: Validate against McKinsey ceiling (67%)
  const successfulCeiling = parseFloat(settings.successful_ceiling);
  const isValid = finalCaptureRate <= successfulCeiling;
  const exceedsBy = isValid ? 0 : finalCaptureRate - successfulCeiling;

  return {
    // Value metrics
    transformationValue,
    totalLoss,
    systemLoss,
    leadershipLoss,

    // Factor scores
    dcf,
    dcfBaseline: dcf,  // For database storage
    lpff,
    lpffCalculated: lpff,  // For database storage
    crf,

    // Recovery calculation
    recoverableValue,
    recoveryPoints,
    finalCaptureRate,

    // Validation
    isValid,
    exceedsBy,
    mckinseySuccessfulCeiling: successfulCeiling,

    // Persona info
    personaId,
    personaRole,
    personaStrain,

    // Algorithm metadata
    algorithmVersion: '7.0'
  };
}

/**
 * Calculate overall anxiety impact score from 10 generic anxiety ratings
 *
 * @param {Object} anxietyScores - Object with anxiety1-anxiety10 scores (0-10 scale)
 * @returns {Object} - { totalScore, averageScore, burdenLevel, anxietyScores: [] }
 */
export function calculateAnxietyImpact(anxietyScores) {
  // 10 generic anxieties from database
  const anxietyNames = [
    'Imposter Complex',
    'Fragile Authority',
    'Over-Dependence',
    'Moral Injury',
    'Invisible Labour',
    'Failing the Future',
    'Team Disengagement',
    'Personal Depletion',
    'Identity Drift',
    'Loss of Meaning'
  ];

  let totalScore = 0;
  const scoredAnxieties = [];

  for (let i = 1; i <= 10; i++) {
    const score = anxietyScores[`anxiety${i}`] || 0;
    totalScore += score;
    scoredAnxieties.push({
      id: i,
      name: anxietyNames[i - 1],
      score: score
    });
  }

  const averageScore = totalScore / 10;

  // Determine burden level based on average
  let burdenLevel;
  let burdenDescription;

  if (averageScore <= 3) {
    burdenLevel = 'Low';
    burdenDescription = 'Manageable anxiety levels. Leadership burden is within sustainable range.';
  } else if (averageScore <= 6) {
    burdenLevel = 'Moderate';
    burdenDescription = 'Notable anxiety burden. Some areas require attention to prevent escalation.';
  } else if (averageScore <= 8) {
    burdenLevel = 'High';
    burdenDescription = 'Significant anxiety burden. Immediate intervention recommended.';
  } else {
    burdenLevel = 'Critical';
    burdenDescription = 'Critical anxiety burden. Urgent support and intervention required.';
  }

  return {
    totalScore: Math.round(totalScore * 10) / 10,
    averageScore: Math.round(averageScore * 10) / 10,
    burdenLevel,
    burdenDescription,
    anxietyScores: scoredAnxieties
  };
}

/**
 * Calculate anxiety theme scores based on theme-to-anxiety mappings
 *
 * @param {Object} anxietyScores - Object with anxiety1-anxiety10 scores
 * @param {Array} themeMapping - Array of theme objects from database with genericAnxietyId
 * @returns {Array} - Array of themes with calculated scores
 */
export function calculateThemeScores(anxietyScores, themeMapping) {
  // Group themes by genericAnxietyId and calculate average impact
  const themeScores = themeMapping.map(theme => {
    const anxietyScore = anxietyScores[`anxiety${theme.genericAnxietyId}`] || 0;

    return {
      id: theme.id,
      anxietyTheme: theme.anxietyTheme,
      genericAnxietyId: theme.genericAnxietyId,
      personaID: theme.personaID,
      hiddenWorkType: theme.hiddenWorkType,
      score: anxietyScore,
      impactLevel: getAnxietyImpactLevel(anxietyScore)
    };
  });

  // Sort by score descending to show highest burden themes first
  themeScores.sort((a, b) => b.score - a.score);

  return themeScores;
}

/**
 * Get impact level classification for an anxiety score
 *
 * @param {number} score - Anxiety score (0-10)
 * @returns {Object} - { level, color, description }
 */
export function getAnxietyImpactLevel(score) {
  if (score <= 3) {
    return {
      level: 'Low',
      color: '#10b981', // green
      description: 'Minimal impact - within normal range'
    };
  } else if (score <= 6) {
    return {
      level: 'Moderate',
      color: '#f59e0b', // amber
      description: 'Moderate impact - requires monitoring'
    };
  } else if (score <= 8) {
    return {
      level: 'High',
      color: '#ef4444', // red
      description: 'High impact - intervention recommended'
    };
  } else {
    return {
      level: 'Critical',
      color: '#991b1b', // dark red
      description: 'Critical impact - urgent attention required'
    };
  }
}

/**
 * Get top 3 highest anxiety scores for insights (legacy)
 *
 * @param {Object} anxietyScores - Object with anxiety1-anxiety10 scores
 * @returns {Array} - Top 3 anxieties with details
 */
export function getTopAnxieties(anxietyScores) {
  const anxietyNames = [
    'Imposter Complex',
    'Fragile Authority',
    'Over-Dependence',
    'Moral Injury',
    'Invisible Labour',
    'Failing the Future',
    'Team Disengagement',
    'Personal Depletion',
    'Identity Drift',
    'Loss of Meaning'
  ];

  const anxietyDescriptions = [
    'Fear of exposure, inadequacy beliefs',
    'Trust uncertainty, performative leadership',
    'System collapse fears, boundary erosion',
    'Values conflict, integrity erosion',
    'Recognition hunger, emotional containment',
    'Legacy anxiety, wasted effort fears',
    'Learned helplessness, ownership avoidance',
    'Burnout, lack of renewal',
    'Role fusion, self-loss',
    'Existential fatigue, purpose disconnection'
  ];

  const actInterventions = [
    'Defusion from "I\'m not good enough"; Values work',
    'Self-as-context; Acceptance of transference',
    'Boundary work; Defusion from control thoughts',
    'Values clarification; Committed action alignment',
    'Acceptance; Self-compassion practices',
    'Present moment awareness; Values over outcomes',
    'Perspective-taking; Empowerment reframing',
    'Self-care as values; Sustainable pace work',
    'Self-as-context; Role vs self differentiation',
    'Values reconnection; Meaningful action'
  ];

  const scoredAnxieties = [];

  for (let i = 1; i <= 10; i++) {
    const score = anxietyScores[`anxiety${i}`] || 0;
    scoredAnxieties.push({
      id: i,
      name: anxietyNames[i - 1],
      description: anxietyDescriptions[i - 1],
      intervention: actInterventions[i - 1],
      score: score
    });
  }

  // Sort by score descending and take top 3
  scoredAnxieties.sort((a, b) => b.score - a.score);
  return scoredAnxieties.slice(0, 3);
}

/**
 * Get top 3 highest anxiety scores for intervention priorities (v7.0)
 *
 * @param {Object} anxietyScores - 10 anxiety scores
 * @returns {Array} Top 3 anxieties with highest scores
 */
export function getTopAnxietiesV2(anxietyScores) {
  return getTopAnxieties(anxietyScores);  // Same implementation
}

// ============================================================================
// PSYCHOLOGICAL FLEXIBILITY DIAGNOSTIC (Algorithm v7.0)
// ============================================================================

/**
 * Calculate psychological flexibility score from 10 flexibility measures
 * Based on ACT (Acceptance and Commitment Therapy) core processes
 *
 * @param {Object} flexScores - Object with flex1-flex10 scores (0-10 scale)
 * @returns {Object} - { totalScore, averageScore, flexibilityLevel, flexibilityDescription, measures: [] }
 */
export function calculateFlexibilityScore(flexScores) {
  const flexibilityNames = [
    'Present Moment Awareness',
    'Cognitive Defusion',
    'Acceptance',
    'Self-as-Context',
    'Values Clarity',
    'Committed Action',
    'Psychological Flexibility',
    'Experiential Avoidance (Reversed)',
    'Cognitive Fusion (Reversed)',
    'Inaction (Reversed)'
  ];

  let totalScore = 0;
  const scoredMeasures = [];

  for (let i = 1; i <= 10; i++) {
    const score = flexScores[`flex${i}`] || 0;
    // Reverse scores for measures 8-10 (lower is better for these)
    const adjustedScore = i >= 8 ? (10 - score) : score;
    totalScore += adjustedScore;

    scoredMeasures.push({
      id: i,
      name: flexibilityNames[i - 1],
      score: score,
      adjustedScore: adjustedScore,
      isReversed: i >= 8
    });
  }

  const averageScore = totalScore / 10;
  let flexibilityLevel, flexibilityDescription;

  if (averageScore >= 8) {
    flexibilityLevel = 'High';
    flexibilityDescription = 'Strong psychological flexibility. Well-equipped to handle challenges with adaptive responses.';
  } else if (averageScore >= 6) {
    flexibilityLevel = 'Moderate';
    flexibilityDescription = 'Moderate psychological flexibility. Some capacity for adaptive responses, with room for growth.';
  } else if (averageScore >= 4) {
    flexibilityLevel = 'Low';
    flexibilityDescription = 'Low psychological flexibility. May struggle with rigid patterns. Intervention recommended.';
  } else {
    flexibilityLevel = 'Very Low';
    flexibilityDescription = 'Very low psychological flexibility. Significant rigidity in responses. Therapeutic support strongly recommended.';
  }

  return {
    totalScore: Math.round(totalScore * 10) / 10,
    averageScore: Math.round(averageScore * 10) / 10,
    flexibilityLevel,
    flexibilityDescription,
    measures: scoredMeasures
  };
}

/**
 * Calculate Psychological Flexibility results using v7.0 algorithm
 *
 * Standalone wellbeing assessment - no transformation value required
 * Measures psychological inflexibility (higher score = more inflexible)
 *
 * @param {Object} flexibilityScores - 10 flexibility scores { flex1: 0-10, ..., flex10: 0-10 }
 * @returns {Object} Wellbeing assessment results
 */
export function calculatePsychologicalFlexibilityV2(flexibilityScores) {
  // All items measure psychological inflexibility (high score = inflexible)
  // IMPORTANT: Scores are inverted because questions ask "How often does problem occur?"
  // where 10 = "Constantly" (worst inflexibility) and 0 = "Never" (best flexibility)
  // We invert so high problem frequency → high inflexibility score (correct)
  const scoreValues = Object.values(flexibilityScores);
  const invertedScore = scoreValues.reduce((sum, score) => sum + (10 - score), 0);
  const totalScore = invertedScore;
  const averageScore = totalScore / 10;

  // Determine burden level based on average inflexibility
  let burdenLevel, burdenDescription, recommendations;

  if (averageScore >= 7.5) {
    burdenLevel = 'Critical';
    burdenDescription = 'Critical psychological inflexibility. Significant rigidity in thought and behavior patterns. Urgent therapeutic support strongly recommended.';
    recommendations = [
      'Immediate professional support recommended',
      'Consider ACT-based therapy or coaching',
      'Regular wellbeing check-ins essential',
      'Reduce non-essential stressors where possible'
    ];
  } else if (averageScore >= 5.0) {
    burdenLevel = 'High';
    burdenDescription = 'High psychological inflexibility. Notable difficulty with adaptive responses to challenges. Proactive intervention recommended.';
    recommendations = [
      'Proactive intervention recommended',
      'Build psychological flexibility skills',
      'Regular mindfulness or meditation practice',
      'Clarify and recommit to core values'
    ];
  } else if (averageScore >= 2.5) {
    burdenLevel = 'Moderate';
    burdenDescription = 'Moderate psychological inflexibility. Some capacity for adaptive responses, with room for growth in flexibility.';
    recommendations = [
      'Continue building flexibility skills',
      'Maintain awareness of triggers',
      'Strengthen acceptance practices',
      'Regular reflection on values alignment'
    ];
  } else {
    burdenLevel = 'Low';
    burdenDescription = 'Low psychological inflexibility. Strong capacity for adaptive, values-based responses to challenges.';
    recommendations = [
      'Maintain current wellbeing practices',
      'Continue values-based living',
      'Share strategies with others',
      'Use as baseline for future comparison'
    ];
  }

  return {
    totalScore: Math.round(totalScore * 10) / 10,
    averageScore: Math.round(averageScore * 10) / 10,
    burdenLevel,
    burdenDescription,
    recommendations,
    psychologicalInflexibilityFactor: averageScore / 10,  // 0-1 scale for database
    algorithmVersion: '7.0'
  };
}

/**
 * Calculate wellbeing burden indicator combining flexibility with anxiety/value
 *
 * @param {Object} flexScores - Object with flex1-flex10 scores
 * @param {number} averageAnxietyScore - Average anxiety from previous diagnostics (optional)
 * @param {number} valueAtStake - Perceived value at stake (optional)
 * @returns {Object} - { wellbeingBurden, riskLevel, recommendations }
 */
export function calculateWellbeingBurden(flexScores, averageAnxietyScore = null, valueAtStake = null) {
  const flexibility = calculateFlexibilityScore(flexScores);

  // Base wellbeing burden on flexibility (inverse relationship)
  // High flexibility = low burden, Low flexibility = high burden
  let wellbeingBurden = 10 - flexibility.averageScore;

  // Adjust if anxiety data available (higher anxiety = higher burden)
  if (averageAnxietyScore !== null) {
    wellbeingBurden = (wellbeingBurden + averageAnxietyScore) / 2;
  }

  // Adjust if value at stake data available
  if (valueAtStake !== null && valueAtStake > 0) {
    // Higher stakes amplify the burden
    const stakeMultiplier = 1 + (valueAtStake / 20); // Max 1.5x multiplier
    wellbeingBurden = Math.min(wellbeingBurden * stakeMultiplier, 10);
  }

  wellbeingBurden = Math.round(wellbeingBurden * 10) / 10;

  let riskLevel, recommendations;

  if (wellbeingBurden >= 7.5) {
    riskLevel = 'Critical';
    recommendations = [
      'Immediate professional support recommended',
      'Consider ACT-based therapy or coaching',
      'Regular wellbeing check-ins essential',
      'Reduce non-essential stressors where possible'
    ];
  } else if (wellbeingBurden >= 5) {
    riskLevel = 'High';
    recommendations = [
      'Proactive intervention recommended',
      'Build psychological flexibility skills',
      'Regular mindfulness or meditation practice',
      'Clarify and recommit to core values'
    ];
  } else if (wellbeingBurden >= 2.5) {
    riskLevel = 'Moderate';
    recommendations = [
      'Continue building flexibility skills',
      'Maintain awareness of triggers',
      'Strengthen acceptance practices',
      'Regular reflection on values alignment'
    ];
  } else {
    riskLevel = 'Low';
    recommendations = [
      'Maintain current wellbeing practices',
      'Continue values-based living',
      'Share strategies with others',
      'Use as baseline for future comparison'
    ];
  }

  return {
    wellbeingBurden,
    riskLevel,
    recommendations,
    flexibility: flexibility.flexibilityLevel
  };
}

/**
 * Get lowest flexibility measures (areas needing attention) - legacy
 *
 * @param {Object} flexScores - Object with flex1-flex10 scores
 * @returns {Array} - Top 3 measures with lowest adjusted scores
 */
export function getLowestFlexibilityMeasures(flexScores) {
  const flexibilityNames = [
    'Present Moment Awareness',
    'Cognitive Defusion',
    'Acceptance',
    'Self-as-Context',
    'Values Clarity',
    'Committed Action',
    'Psychological Flexibility',
    'Experiential Avoidance (Reversed)',
    'Cognitive Fusion (Reversed)',
    'Inaction (Reversed)'
  ];

  const interventions = [
    'Mindfulness meditation; Grounding exercises; Present-moment anchoring',
    'Thought labeling; "I\'m having the thought that..." practice; Metaphor work',
    'Willingness practices; "Yes, and..." exercises; Expansion techniques',
    'Observer self exercises; Perspective-taking; Transcendent self work',
    'Values clarification exercises; Bull\'s-eye values assessment; Values-based goals',
    'SMART goals aligned with values; Behavioral activation; Barrier identification',
    'Hexaflex integration; Multi-process flexibility work; ACT in daily life',
    'Approach behavior practice; Exposure exercises; Willingness-based action',
    'Cognitive flexibility training; Alternative perspectives; Mental flexibility',
    'Behavioral activation; Small steps practice; Values-driven momentum'
  ];

  const scoredMeasures = [];

  for (let i = 1; i <= 10; i++) {
    const score = flexScores[`flex${i}`] || 0;
    // For reversed items (8-10), lower score means better, so invert for ranking
    const adjustedScore = i >= 8 ? (10 - score) : score;

    scoredMeasures.push({
      id: i,
      name: flexibilityNames[i - 1],
      score: score,
      adjustedScore: adjustedScore,
      intervention: interventions[i - 1],
      isReversed: i >= 8
    });
  }

  // Sort by adjusted score ascending (lowest = needs most attention)
  scoredMeasures.sort((a, b) => a.adjustedScore - b.adjustedScore);
  return scoredMeasures.slice(0, 3);
}

/**
 * Get 3 areas with highest inflexibility scores for intervention (v7.0)
 *
 * @param {Object} flexibilityScores - 10 flexibility scores
 * @returns {Array} Top 3 areas with highest inflexibility
 */
export function getHighestInflexibilityAreas(flexibilityScores) {
  const flexibilityNames = [
    'Present Moment Awareness',
    'Cognitive Defusion',
    'Acceptance',
    'Self-as-Context',
    'Values Clarity',
    'Committed Action',
    'Psychological Flexibility',
    'Experiential Avoidance',
    'Cognitive Fusion',
    'Inaction'
  ];

  const interventions = [
    'Mindfulness meditation; Grounding exercises; Present-moment anchoring',
    'Thought labeling; "I\'m having the thought that..." practice; Metaphor work',
    'Willingness practices; "Yes, and..." exercises; Expansion techniques',
    'Observer self exercises; Perspective-taking; Transcendent self work',
    'Values clarification exercises; Bull\'s-eye values assessment; Values-based goals',
    'SMART goals aligned with values; Behavioral activation; Barrier identification',
    'Hexaflex integration; Multi-process flexibility work; ACT in daily life',
    'Approach behavior practice; Exposure exercises; Willingness-based action',
    'Cognitive flexibility training; Alternative perspectives; Mental flexibility',
    'Behavioral activation; Small steps practice; Values-driven momentum'
  ];

  const scoredMeasures = flexibilityNames.map((name, index) => ({
    id: index + 1,
    name,
    score: flexibilityScores[`flex${index + 1}`] || 0,
    intervention: interventions[index]
  }));

  // Sort by score descending (highest = most inflexible = needs most attention)
  scoredMeasures.sort((a, b) => b.score - a.score);

  return scoredMeasures.slice(0, 3);
}

// ============================================================================
// ALGORITHM v7.1 - SIMPLIFIED CALCULATION
// ============================================================================

// Simplified algorithm constants
export const SIMPLIFIED_CONSTANTS = {
  // Fixed value segments (McKinsey-derived)
  PERMANENTLY_LOST: 0.33,      // Always lost (33%)
  STRETCH_POTENTIAL: 0.10,     // Recoverable through intervention (10%)
  BASELINE_CAPTURED: 0.57,     // Minimum capture (average transformation)
  MAXIMUM_CAPTURED: 0.67,      // Maximum capture (excellent transformation)

  // Valid interpolation zones for DCF and LPFF
  DCF_MIN: 0.70,   // Below this = Foundational
  DCF_MAX: 0.85,   // Above this = Mastery
  LPFF_MIN: 0.50,  // Below this = Foundational
  LPFF_MAX: 0.85,  // Above this = Mastery
};

/**
 * Calculate results using the simplified v7.1 algorithm
 *
 * Key insight: The pie chart segments are fixed:
 * - Permanently Lost = 33% (always)
 * - Stretch Potential = 10% (maximum recoverable)
 * - Baseline Captured = 57% (average transformation)
 * - Maximum Captured = 67% (excellent transformation)
 *
 * DCF and LPFF position within their valid zones determines
 * where on the 57%-67% range the capture rate falls.
 *
 * @param {number} valueAtStake - Value at stake (£M)
 * @param {number} dcf - Dependency Competence Factor (0-1)
 * @param {number} lpff - Leadership Psychological Flexibility Factor (0-1)
 * @returns {Object} Simplified calculation results
 */
export function calculateSimplifiedResults(valueAtStake, dcf, lpff) {
  const {
    PERMANENTLY_LOST, STRETCH_POTENTIAL, BASELINE_CAPTURED, MAXIMUM_CAPTURED,
    DCF_MIN, DCF_MAX, LPFF_MIN, LPFF_MAX
  } = SIMPLIFIED_CONSTANTS;

  // Helper function to clamp values
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  // Step 1: Normalize DCF and LPFF to 0-1 within their valid zones
  const dcfNormalized = clamp((dcf - DCF_MIN) / (DCF_MAX - DCF_MIN), 0, 1);
  const lpffNormalized = clamp((lpff - LPFF_MIN) / (LPFF_MAX - LPFF_MIN), 0, 1);

  // Step 2: Calculate combined factor using McKinsey-derived weights (68.5% system, 31.5% leadership)
  const combinedFactor = (dcfNormalized * SYSTEM_LOSS_WEIGHT) + (lpffNormalized * LEADERSHIP_LOSS_WEIGHT);

  // Step 3: Map combined factor to capture rate (0.57 to 0.67)
  const captureRate = BASELINE_CAPTURED + (combinedFactor * STRETCH_POTENTIAL);

  // Step 4: Calculate pie segments (these always sum to valueAtStake)
  const captured = valueAtStake * captureRate;
  const stretchPotential = valueAtStake * (MAXIMUM_CAPTURED - captureRate);
  const permanentlyLost = valueAtStake * PERMANENTLY_LOST;

  // Step 5: Calculate intervention breakdown (from stretch potential only)
  const systemRecoverable = stretchPotential * SYSTEM_LOSS_WEIGHT;
  const leadershipRecoverable = stretchPotential * LEADERSHIP_LOSS_WEIGHT;

  // Determine zone based on capture rate
  const getZone = (rate) => {
    if (rate < 0.60) return 'foundational';  // Near baseline
    if (rate < 0.64) return 'stretch';       // Middle of range
    return 'mastery';                         // Near maximum
  };

  return {
    // Inputs
    valueAtStake,
    dcf,
    lpff,

    // Normalized positions (0 = bottom of zone, 1 = top of zone)
    dcfNormalized,
    lpffNormalized,
    combinedFactor,

    // Capture rate (0.57 to 0.67)
    captureRate,

    // Pie segments (should sum to valueAtStake)
    captured,
    stretchPotential,
    permanentlyLost,

    // Fixed percentages for display
    capturedPercent: captureRate,
    stretchPotentialPercent: MAXIMUM_CAPTURED - captureRate,
    permanentlyLostPercent: PERMANENTLY_LOST,

    // Recoverable breakdown (from stretch potential)
    systemRecoverable,
    leadershipRecoverable,
    totalRecoverable: stretchPotential,

    // Zone determination
    zone: getZone(captureRate),

    // Constants for reference
    constants: SIMPLIFIED_CONSTANTS,

    // Algorithm metadata
    algorithmVersion: '7.1'
  };
}

// ============================================================================
// VALIDATION & FORMATTING UTILITIES
// ============================================================================

/**
 * Validate that final capture rate is within empirical bounds
 *
 * @param {number} finalCaptureRate - Calculated final capture rate (0-1)
 * @returns {Object} Validation result with recommendations
 */
export function validateCaptureRate(finalCaptureRate) {
  const isValid = finalCaptureRate <= SUCCESSFUL_CEILING;
  const exceedsBy = isValid ? 0 : finalCaptureRate - SUCCESSFUL_CEILING;

  let recommendation = '';
  if (!isValid) {
    const percentageOver = (exceedsBy * 100).toFixed(1);
    recommendation = `Final capture rate (${(finalCaptureRate * 100).toFixed(1)}%) exceeds empirical ceiling (${(SUCCESSFUL_CEILING * 100).toFixed(1)}%) by ${percentageOver}%. ` +
      `This suggests scores may be overestimating capability. Please review dependency/anxiety scores for accuracy.`;
  }

  return {
    isValid,
    exceedsBy,
    ceiling: SUCCESSFUL_CEILING,
    recommendation
  };
}

/**
 * Get persona baseline information
 *
 * @param {string} personaId - 'mark' or 'sarah'
 * @returns {Object} Persona baseline data
 */
export function getPersonaBaseline(personaId) {
  const persona = PERSONA_BASELINES[personaId];
  if (!persona) {
    throw new Error(`Invalid persona ID: ${personaId}. Must be 'mark' or 'sarah'.`);
  }
  return { ...persona };
}

/**
 * Format value for display (£M with 2 decimal places)
 *
 * @param {number} value - Value in millions
 * @returns {string} Formatted value
 */
export function formatValue(value) {
  return `£${value.toFixed(2)}M`;
}

/**
 * Format percentage for display (with 1 decimal place)
 *
 * @param {number} decimal - Decimal value (0-1)
 * @returns {string} Formatted percentage
 */
export function formatPercentage(decimal) {
  return `${(decimal * 100).toFixed(1)}%`;
}

/**
 * Calculate breakdown of CRF components for visualization
 *
 * @param {number} dcf - Dependency Competence Factor
 * @param {number} lpff - Leadership Psychological Flexibility Factor
 * @returns {Object} Breakdown of CRF components
 */
export function calculateCRFBreakdown(dcf, lpff) {
  const systemComponent = dcf * SYSTEM_LOSS_WEIGHT;
  const leadershipComponentBase = lpff * LEADERSHIP_LOSS_WEIGHT;
  const actEffect = leadershipComponentBase * (ACT_COACHING_MULTIPLIER - 1);  // The 8% boost
  const leadershipComponentTotal = leadershipComponentBase * ACT_COACHING_MULTIPLIER;
  const crf = systemComponent + leadershipComponentTotal;

  return {
    systemComponent,
    systemPercentage: SYSTEM_LOSS_WEIGHT,
    leadershipComponentBase,
    leadershipComponentTotal,
    leadershipPercentage: LEADERSHIP_LOSS_WEIGHT,
    actEffect,
    actMultiplier: ACT_COACHING_MULTIPLIER,
    crf
  };
}

// ============================================================================
// HUBSPOT DATA PREPARATION (Legacy)
// ============================================================================

export function prepareHubSpotData(formData, calculations) {
  const diagnosticData = {
    timestamp: new Date().toISOString(),
    version: '7.0',
    role: formData.role,
    valueAtStake: formData.valueAtStake,
    scores: {
      score1: formData.score1, score2: formData.score2, score3: formData.score3,
      score4: formData.score4, score5: formData.score5, score6: formData.score6,
      score7: formData.score7, score8: formData.score8, score9: formData.score9,
      score10: formData.score10, score11: formData.score11, score12: formData.score12,
      score13: formData.score13, score14: formData.score14
    },
    calculations: {
      valueLost: calculations.valueLost,
      recoverabilityFactorPercentage: calculations.recoverabilityFactorPercentage,
      valueTheoreticallyRecoverable: calculations.valueTheoreticallyRecoverable,
      dependencyImpactFactor: calculations.dependencyImpactFactor,
      modifiedRecoverabilityPercentage: calculations.modifiedRecoverabilityPercentage,
      valueCurrentCapabilityRecoverable: calculations.valueCurrentCapabilityRecoverable,
      valueDeficit: calculations.valueDeficit
    }
  };

  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    diagnosticData: JSON.stringify(diagnosticData),
    diagnosticLastCompleted: new Date().toISOString().split('T')[0]
  };
}
